<template>
  <div class="space-y-3">
    <div
      v-for="category in props.categories"
      :key="category.id"
      class="flex justify-between gap-4"
    >
      <div class="flex items-center gap-2 justify-between flex-1 min-w-0">
        <div>
          <div class="font-medium text-xs" :class="[{ truncate: compact }]">
            {{ category.displayLabel }}
          </div>

          <div class="text-gray-600 text-xs flex items-center gap-1">
            <span
              v-if="category.displayOriginalPrice"
              class="line-through text-gray-400"
            >
              {{ category.displayOriginalPrice }}
            </span>
            <div
              v-if="category.displayPrice && !shouldShowSkeleton(category)"
              class="text-teal-900"
              :class="{ '!text-teal-600': category.displayOriginalPrice }"
            >
              {{ category.displayPrice }}
            </div>

            <div
              v-else-if="shouldShowSkeleton(category)"
              class="animate-pulse bg-gray-300 rounded"
              :class="size === 'lg' ? 'h-4 w-16' : 'h-3 w-12 mt-1'"
            />
          </div>
        </div>
      </div>

      <NumberControlButtons
        v-model="quantities[category.ticket_category]"
        class="flex-shrink-0"
        :size="size"
        :min="0"
        :max="
          maxQty
            ? quantities[category.ticket_category] + maxQty - totalTickets
            : undefined
        "
        :disable-minus="
          quantities[category.ticket_category] <= 0 ||
          (quantities[category.ticket_category] === 1 && totalTickets === 1)
        "
        :is-dates-loaded="isDatesLoaded"
        @max-reached="handleMaxReached"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
export interface CategoryItem {
  id: string
  ticket_category: string
  displayLabel: string
  displayPrice?: string
  displayOriginalPrice?: string
  amountOffPercentage?: number
}

const props = defineProps<{
  categories: CategoryItem[]
  maxQty?: number
  isDatesLoaded?: boolean
  compact?: boolean
  size?: 'sm' | 'lg'
  loadingPricings?: boolean
}>()

const size = computed(() => props.size ?? 'sm')

const emit = defineEmits<{
  (e: 'max-reached'): void
}>()

const quantities = defineModel<Record<string, number>>({ required: true })

const totalTickets = computed(() => {
  return Object.values(quantities.value).reduce((sum, qty) => sum + qty, 0)
})

const handleMaxReached = () => {
  emit('max-reached')
}

const shouldShowSkeleton = (category: CategoryItem) => {
  const categoryQuantity = quantities.value[category.ticket_category] || 0

  // Show skeleton when category has quantity > 0 but no price available
  // This works even during debounce delay (before request starts)
  // and during actual loading
  return categoryQuantity > 0 && !category.displayPrice
}

// Watch for when user tries to go beyond max
// Use a getter that returns a deep copy so we get proper old/new values
watch(
  () => JSON.parse(JSON.stringify(quantities.value)) as Record<string, number>,
  (newQuantities, oldQuantities) => {
    if (props.maxQty === undefined) return

    const newTotal = Object.values(newQuantities).reduce(
      (sum: number, qty: number) => sum + qty,
      0
    )

    // If total exceeds max, revert to old quantities
    if (newTotal > props.maxQty && oldQuantities) {
      Object.keys(quantities.value).forEach((key) => {
        quantities.value[key] = oldQuantities[key] || 0
      })
      emit('max-reached')
    }
  }
)

// Ensure at least 1 ticket is always selected
watch(
  quantities,
  (newQuantities) => {
    const total = Object.values(newQuantities).reduce(
      (sum, qty) => sum + qty,
      0
    )

    // If total is 0, set first category to 1
    if (total === 0 && props.categories.length > 0) {
      const firstCategory = props.categories[0].ticket_category
      quantities.value[firstCategory] = 1
    }
  },
  { deep: true }
)
</script>
